import React, { useCallback, useEffect, useMemo, useState } from "react";
import styled, { css, keyframes } from "styled-components";
import { Modal, CircularProgress, Tooltip } from "@material-ui/core";
import {
  CheckCircle,
  Close,
  GetApp,
  Search,
  Description,
  ReceiptOutlined,
  Refresh,
} from "@material-ui/icons";
import { useSnackbar } from "notistack";
import * as XLSX from "xlsx";
import _ from "lodash";
import moment from "moment";
import CaseService from "../../../../services/CaseService";
import { getErrorMessage, getFileName } from "../../../../helpers/functions";
import theme from "../../../../assets/theme";
import COLORS from "../../../../assets/Colors";
import { CustomCheckbox } from "../../../common/FormInputs";

const PAGE_SIZE = 25;

const SUMMARY_ITEMS = [
  { key: "totalCases", label: "Total Cases", tone: "dark" },
  { key: "part2ApprovedCases", label: "Part 2 Approved", tone: "green" },
  { key: "awardDocumentCount", label: "Award Documents", tone: "blue" },
  { key: "awardUploadedCases", label: "Award Uploaded", tone: "blue" },
  {
    key: "awardPassedOrFinalAwardCases",
    label: "Award Passed / Final Award",
    tone: "purple",
  },
  {
    key: "awardUploadedAndPassedCases",
    label: "Award Uploaded & Passed",
    tone: "purple",
  },
  {
    key: "arbitratorAppointedAndAwardPassedCases",
    label: "Arbitrator Appointed & Award Passed",
    tone: "orange",
  },
  {
    key: "withdrawnAndArbitratorAppointedCases",
    label: "Withdrawn & Arbitrator Appointed",
    tone: "red",
  },
];

// awardFiles may come back as plain urls or as file objects
const getAwardUrl = (file) =>
  typeof file === "string"
    ? file
    : file?.url || file?.fileUrl || file?.awardUrl || "";

const getAwardName = (file) =>
  (typeof file === "object" && (file?.name || file?.fileName)) ||
  decodeURI(getFileName(getAwardUrl(file)) || "Award");

const Part2InvoiceModal = ({ open, onClose, documentId, fileName }) => {
  const { enqueueSnackbar } = useSnackbar();
  const [loading, setLoading] = useState(false);
  const [batch, setBatch] = useState(null);
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(0);
  const [selected, setSelected] = useState([]);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [approving, setApproving] = useState(false);

  const fetchReview = useCallback(async () => {
    if (!documentId) return;
    try {
      setLoading(true);
      const response = await CaseService.getPart2Review(documentId);
      const batches = response?.batches || [];
      setBatch(
        batches.find((b) => b?.documentId === documentId) || batches[0] || null,
      );
    } catch (error) {
      enqueueSnackbar(getErrorMessage(error) || "Unable to load Part 2 review", {
        variant: "error",
      });
    } finally {
      setLoading(false);
    }
  }, [documentId, enqueueSnackbar]);

  useEffect(() => {
    if (open) fetchReview();
  }, [open, fetchReview]);

  const cases = useMemo(() => batch?.cases || [], [batch]);

  const filteredCases = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return cases;
    return cases.filter((c) =>
      [
        c.caseId,
        c.contractNumber,
        c.status,
        _.startCase(c.status),
        ...(c.arbitratorNames || []),
      ]
        .filter(Boolean)
        .some((v) => String(v).toLowerCase().includes(term)),
    );
  }, [cases, search]);

  const totalPages = Math.max(1, Math.ceil(filteredCases.length / PAGE_SIZE));
  const pageCases = filteredCases.slice(
    page * PAGE_SIZE,
    page * PAGE_SIZE + PAGE_SIZE,
  );

  useEffect(() => {
    setPage(0);
  }, [search]);

  const selectableIds = filteredCases
    .filter((c) => !c.partInvoiceApproval)
    .map((c) => c.caseId);
  const allSelected =
    selectableIds.length > 0 &&
    selectableIds.every((id) => selected.includes(id));
  const someSelected =
    !allSelected && selectableIds.some((id) => selected.includes(id));

  const toggleAll = () =>
    setSelected((prev) =>
      allSelected
        ? prev.filter((id) => !selectableIds.includes(id))
        : _.uniq([...prev, ...selectableIds]),
    );

  const toggleRow = (caseId) =>
    setSelected((prev) =>
      prev.includes(caseId)
        ? prev.filter((id) => id !== caseId)
        : [...prev, caseId],
    );

  const exportExcel = () => {
    const rows = cases.map((c, index) => ({
      "S.No": index + 1,
      "Case ID": c.caseId,
      "Contract Number": c.contractNumber || "",
      Status: _.startCase(c.status),
      "Arbitrator Names": (c.arbitratorNames || []).join(", "),
      "Award Files": (c.awardFiles || []).map(getAwardUrl).join(", "),
      "Part 2 Invoice Approved": c.partInvoiceApproval ? "Yes" : "No",
    }));
    const summary = batch?.summary || {};
    const summaryRows = [
      { Metric: "Document ID", Value: batch?.documentId },
      {
        Metric: "Uploaded At",
        Value: batch?.uploadedAt
          ? moment(batch.uploadedAt).format("DD/MM/YYYY hh:mm A")
          : "",
      },
      ...SUMMARY_ITEMS.map((item) => ({
        Metric: item.label,
        Value: summary[item.key] ?? 0,
      })),
    ];
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(rows), "Cases");
    XLSX.utils.book_append_sheet(
      wb,
      XLSX.utils.json_to_sheet(summaryRows),
      "Summary",
    );
    XLSX.writeFile(wb, `Part2_Review_${documentId}.xlsx`);
  };

  const approveSelected = async () => {
    try {
      setApproving(true);
      const response = await CaseService.part2Approval({
        documentId,
        caseIds: selected,
      });
      enqueueSnackbar(
        response?.message || "Part 2 approved for selected cases",
        { variant: "success" },
      );
      setSelected([]);
      setConfirmOpen(false);
      fetchReview();
    } catch (error) {
      enqueueSnackbar(getErrorMessage(error) || "Unable to approve Part 2", {
        variant: "error",
      });
    } finally {
      setApproving(false);
    }
  };

  const summary = batch?.summary || {};

  return (
    <Modal open={open} onClose={onClose} disableBackdropClick={approving}>
      <Card>
        <Header>
          <HeaderLeft>
            <IconBadge>
              <ReceiptOutlined fontSize="small" />
            </IconBadge>
            <div>
              <Title>Part 2 Invoice Review</Title>
              <SubTitle>
                Document #{documentId}
                {fileName ? ` · ${fileName}` : ""}
                {batch?.uploadedAt
                  ? ` · Uploaded ${moment(batch.uploadedAt).format(
                      "DD MMM YYYY, hh:mm A",
                    )}`
                  : ""}
              </SubTitle>
            </div>
          </HeaderLeft>
          <HeaderActions>
            <Tooltip title="Refresh">
              <span>
                <IconBtn onClick={fetchReview} disabled={loading}>
                  <Refresh fontSize="small" />
                </IconBtn>
              </span>
            </Tooltip>
            <IconBtn onClick={onClose} danger>
              <Close fontSize="small" />
            </IconBtn>
          </HeaderActions>
        </Header>

        <Body>
          <SummaryGrid>
            {SUMMARY_ITEMS.map((item) => (
              <SummaryCard key={item.key} tone={item.tone}>
                <SummaryValue tone={item.tone}>
                  {summary[item.key] ?? 0}
                </SummaryValue>
                <SummaryLabel>{item.label}</SummaryLabel>
              </SummaryCard>
            ))}
          </SummaryGrid>

          <Toolbar>
            <SearchBox>
              <Search style={{ color: "#94a3b8", fontSize: 18 }} />
              <input
                placeholder="Search case ID, contract no, status, arbitrator"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </SearchBox>
            <ToolbarRight>
              {selected.length > 0 && (
                <SelectedPill>
                  {selected.length} selected
                  <button onClick={() => setSelected([])}>Clear</button>
                </SelectedPill>
              )}
              <OutlineBtn onClick={exportExcel} disabled={!cases.length}>
                <GetApp style={{ fontSize: 18 }} />
                Export Excel
              </OutlineBtn>
            </ToolbarRight>
          </Toolbar>

          <TableWrap>
            {loading ? (
              <Center>
                <CircularProgress size={28} style={{ color: COLORS.BTN_GREEN }} />
              </Center>
            ) : !filteredCases.length ? (
              <Center>
                <Description style={{ color: "#cbd5e1", fontSize: 40 }} />
                <span>No cases found</span>
              </Center>
            ) : (
              <Table>
                <thead>
                  <tr>
                    <th style={{ width: 44 }}>
                      <CustomCheckbox
                        type="checkbox"
                        checked={allSelected}
                        ref={(el) => el && (el.indeterminate = someSelected)}
                        disabled={!selectableIds.length}
                        onChange={toggleAll}
                      />
                    </th>
                    <th style={{ width: 90 }}>Case ID</th>
                    <th>Contract Number</th>
                    <th>Status</th>
                    <th>Arbitrator</th>
                    <th>Award Files</th>
                    <th style={{ width: 130 }}>Part 2 Invoice</th>
                  </tr>
                </thead>
                <tbody>
                  {pageCases.map((c) => {
                    const isSelected = selected.includes(c.caseId);
                    return (
                      <Row
                        key={c.caseId}
                        selected={isSelected}
                        disabled={c.partInvoiceApproval}
                        onClick={() =>
                          !c.partInvoiceApproval && toggleRow(c.caseId)
                        }
                      >
                        <td onClick={(e) => e.stopPropagation()}>
                          <CustomCheckbox
                            type="checkbox"
                            checked={isSelected}
                            disabled={c.partInvoiceApproval}
                            onChange={() => toggleRow(c.caseId)}
                          />
                        </td>
                        <td>
                          <CaseId>{c.caseId}</CaseId>
                        </td>
                        <td>
                          <Mono>{c.contractNumber || "-"}</Mono>
                        </td>
                        <td>
                          <StatusChip>{_.startCase(c.status) || "-"}</StatusChip>
                        </td>
                        <td>
                          {c.arbitratorNames?.length ? (
                            c.arbitratorNames.join(", ")
                          ) : (
                            <Muted>Not appointed</Muted>
                          )}
                        </td>
                        <td onClick={(e) => e.stopPropagation()}>
                          {c.awardFiles?.length ? (
                            <FileList>
                              {c.awardFiles.map((f, i) => (
                                <FileLink
                                  key={i}
                                  href={getAwardUrl(f)}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  title={getAwardName(f)}
                                >
                                  <Description style={{ fontSize: 14 }} />
                                  {getAwardName(f)}
                                </FileLink>
                              ))}
                            </FileList>
                          ) : (
                            <Muted>No files</Muted>
                          )}
                        </td>
                        <td>
                          {c.partInvoiceApproval ? (
                            <Badge approved>
                              <CheckCircle style={{ fontSize: 14 }} />
                              Approved
                            </Badge>
                          ) : (
                            <Badge>Pending</Badge>
                          )}
                        </td>
                      </Row>
                    );
                  })}
                </tbody>
              </Table>
            )}
          </TableWrap>

          {filteredCases.length > PAGE_SIZE && (
            <Pager>
              <span>
                Showing {page * PAGE_SIZE + 1}–
                {Math.min((page + 1) * PAGE_SIZE, filteredCases.length)} of{" "}
                {filteredCases.length}
              </span>
              <div>
                <PageBtn disabled={page === 0} onClick={() => setPage(page - 1)}>
                  Prev
                </PageBtn>
                <PageInfo>
                  {page + 1} / {totalPages}
                </PageInfo>
                <PageBtn
                  disabled={page + 1 >= totalPages}
                  onClick={() => setPage(page + 1)}
                >
                  Next
                </PageBtn>
              </div>
            </Pager>
          )}
        </Body>

        <Footer>
          <FooterNote>
            Select the cases to approve for Part 2 invoice. Already approved
            cases can't be selected.
          </FooterNote>
          <FooterActions>
            <OutlineBtn onClick={onClose}>Cancel</OutlineBtn>
            <PrimaryBtn
              disabled={!selected.length || approving}
              onClick={() => setConfirmOpen(true)}
            >
              <CheckCircle style={{ fontSize: 18 }} />
              Approve{selected.length ? ` (${selected.length})` : ""}
            </PrimaryBtn>
          </FooterActions>
        </Footer>

        {confirmOpen && (
          <ConfirmOverlay
            onClick={(e) =>
              e.target === e.currentTarget && !approving && setConfirmOpen(false)
            }
          >
            <ConfirmBox>
              <Title>Approve Part 2 Invoice?</Title>
              <ConfirmText>
                You are approving Part 2 invoice for{" "}
                <strong>{selected.length}</strong> case
                {selected.length > 1 ? "s" : ""}.
              </ConfirmText>
              <FooterActions>
                <OutlineBtn
                  disabled={approving}
                  onClick={() => setConfirmOpen(false)}
                >
                  Cancel
                </OutlineBtn>
                <PrimaryBtn disabled={approving} onClick={approveSelected}>
                  {approving ? "Approving..." : "Confirm"}
                </PrimaryBtn>
              </FooterActions>
            </ConfirmBox>
          </ConfirmOverlay>
        )}
      </Card>
    </Modal>
  );
};

export default Part2InvoiceModal;

const TONES = {
  dark: { fg: COLORS.COLOR_DARK, bg: "#f1f5f9" },
  green: { fg: "#047857", bg: "#ecfdf5" },
  blue: { fg: "#1d4ed8", bg: "#eff6ff" },
  purple: { fg: "#6d28d9", bg: "#f5f3ff" },
  orange: { fg: "#c2410c", bg: "#fff7ed" },
  red: { fg: "#b91c1c", bg: "#fef2f2" },
};

const popIn = keyframes`
  from { opacity: 0; transform: translate(-50%, -48%) scale(0.98); }
  to { opacity: 1; transform: translate(-50%, -50%) scale(1); }
`;

const Card = styled.div`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: calc(100% - 32px);
  max-width: 1180px;
  max-height: 92vh;
  display: flex;
  flex-direction: column;
  background: #fff;
  border-radius: 18px;
  box-shadow: 0 24px 80px rgba(15, 23, 42, 0.18);
  outline: none;
  overflow: hidden;
  animation: ${popIn} 0.22s ease-out both;
  font-family: ${theme.fonts.primaryFontRegular};
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 18px 24px;
  border-bottom: 1px solid #eef0f4;
`;

const HeaderLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  min-width: 0;
`;

const HeaderActions = styled.div`
  display: flex;
  gap: 8px;
`;

const IconBadge = styled.div`
  width: 40px;
  height: 40px;
  border-radius: 12px;
  background: ${COLORS.BTN_GREEN}18;
  color: ${COLORS.BTN_GREEN};
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
`;

const Title = styled.h3`
  margin: 0;
  font-size: 17px;
  color: ${COLORS.COLOR_DARK};
  font-family: ${theme.fonts.primaryFontExtraBold};
  letter-spacing: -0.2px;
`;

const SubTitle = styled.div`
  margin-top: 2px;
  font-size: 12px;
  color: #64748b;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const IconBtn = styled.button`
  width: 34px;
  height: 34px;
  border: none;
  border-radius: 10px;
  background: #f4f6f8;
  color: #64748b;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.15s;
  &:hover:not(:disabled) {
    background: ${({ danger }) => (danger ? "#fee2e2" : "#e2e8f0")};
    color: ${({ danger }) => (danger ? "#dc2626" : COLORS.COLOR_DARK)};
  }
  &:disabled {
    opacity: 0.5;
    cursor: default;
  }
`;

const Body = styled.div`
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding: 20px 24px;
  overflow: auto;
`;

const SummaryGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(150px, 1fr));
  gap: 10px;
`;

const SummaryCard = styled.div`
  padding: 12px 14px;
  border-radius: 12px;
  background: ${({ tone }) => TONES[tone].bg};
  border: 1px solid ${({ tone }) => TONES[tone].fg}1a;
`;

const SummaryValue = styled.div`
  font-size: 22px;
  line-height: 1.2;
  font-family: ${theme.fonts.primaryFontExtraBold};
  color: ${({ tone }) => TONES[tone].fg};
`;

const SummaryLabel = styled.div`
  margin-top: 4px;
  font-size: 11.5px;
  color: ${COLORS.COLOR_DARK};
  line-height: 1.3;
  font-family: ${theme.fonts.primaryFontRegular}
`;

const Toolbar = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  flex-wrap: wrap;
`;

const ToolbarRight = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
`;

const SearchBox = styled.label`
  display: flex;
  align-items: center;
  gap: 8px;
  flex: 1;
  max-width: 420px;
  min-width: 220px;
  padding: 8px 12px;
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  background: #f8fafc;
  transition: border-color 0.15s;
  &:focus-within {
    border-color: ${COLORS.BTN_GREEN};
    background: #fff;
  }
  input {
    flex: 1;
    border: none;
    outline: none;
    background: transparent;
    font-size: 13px;
    font-family: ${theme.fonts.primaryFontRegular};
    color: ${COLORS.COLOR_DARK};
  }
`;

const SelectedPill = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 6px 12px;
  border-radius: 99px;
  background: ${COLORS.BTN_GREEN}14;
  color: ${COLORS.BTN_GREEN};
  font-size: 12.5px;
  font-weight: 600;
  button {
    border: none;
    background: none;
    color: #64748b;
    cursor: pointer;
    font-size: 12px;
    text-decoration: underline;
    padding: 0;
  }
`;

const buttonBase = css`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 9px 16px;
  border-radius: 10px;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.15s;
  white-space: nowrap;
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const OutlineBtn = styled.button`
  ${buttonBase};
  border: 1px solid #d7dde6;
  background: #fff;
  color: ${COLORS.COLOR_DARK};
  &:hover:not(:disabled) {
    border-color: ${COLORS.BTN_GREEN};
    color: ${COLORS.BTN_GREEN};
  }
`;

const PrimaryBtn = styled.button`
  ${buttonBase};
  border: 1px solid ${COLORS.BTN_GREEN};
  background: ${COLORS.BTN_GREEN};
  color: #fff;
  &:hover:not(:disabled) {
    background: #006f77;
  }
`;

const TableWrap = styled.div`
  border: 1px solid #eef0f4;
  border-radius: 12px;
  overflow: auto;
  max-height: 48vh;
  min-height: 200px;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: separate;
  border-spacing: 0;
  font-size: 13px;
  th {
    position: sticky;
    top: 0;
    z-index: 1;
    background: #f8fafc;
    text-align: left;
    padding: 11px 12px;
    font-size: 11.5px;
    font-weight: 700;
    color: #64748b;
    text-transform: uppercase;
    letter-spacing: 0.4px;
    border-bottom: 1px solid #eef0f4;
  }
  td {
    padding: 11px 12px;
    color: ${COLORS.COLOR_DARK};
    border-bottom: 1px solid #f1f5f9;
    vertical-align: middle;
  }
`;

const Row = styled.tr`
  cursor: ${({ disabled }) => (disabled ? "default" : "pointer")};
  background: ${({ selected }) =>
    selected ? `${COLORS.BTN_GREEN}0d` : "transparent"};
  transition: background 0.12s;
  &:hover {
    background: ${({ selected }) =>
      selected ? `${COLORS.BTN_GREEN}14` : "#fafbfc"};
  }
`;

/*
const Checkbox = styled.input`
  width: 16px;
  height: 16px;
  cursor: pointer;
  accent-color: ${COLORS.BTN_GREEN};
  &:disabled {
    cursor: not-allowed;
  }
`;
*/

const CaseId = styled.span`
  font-weight: 700;
  color: ${COLORS.BTN_GREEN};
`;

const Mono = styled.span`
  font-family: "SFMono-Regular", Consolas, monospace;
  font-size: 12.5px;
`;

const StatusChip = styled.span`
  display: inline-block;
  padding: 3px 10px;
  border-radius: 99px;
  background: #eef2ff;
  color: #3730a3;
  font-size: 12px;
  font-weight: 600;
`;

const Badge = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 3px 10px;
  border-radius: 99px;
  font-size: 12px;
  font-weight: 600;
  background: ${({ approved }) => (approved ? "#ecfdf5" : "#fff7ed")};
  color: ${({ approved }) => (approved ? "#047857" : "#c2410c")};
`;

const Muted = styled.span`
  color: #94a3b8;
  font-size: 12.5px;
`;

const FileList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const FileLink = styled.a`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  max-width: 220px;
  color: ${COLORS.HYPERLINK};
  font-size: 12.5px;
  text-decoration: none;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  &:hover {
    text-decoration: underline;
  }
`;

const Center = styled.div`
  height: 200px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 8px;
  color: #94a3b8;
  font-size: 13px;
`;

const Pager = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  font-size: 12.5px;
  color: #64748b;
  > div {
    display: flex;
    align-items: center;
    gap: 8px;
  }
`;

const PageBtn = styled.button`
  ${buttonBase};
  padding: 6px 12px;
  border: 1px solid #e2e8f0;
  background: #fff;
  color: ${COLORS.COLOR_DARK};
`;

const PageInfo = styled.span`
  font-weight: 600;
  color: ${COLORS.COLOR_DARK};
`;

const Footer = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 14px 24px;
  border-top: 1px solid #eef0f4;
  background: #fbfcfd;
  flex-wrap: wrap;
`;

const FooterNote = styled.span`
  font-size: 12px;
  color: #64748b;
`;

const FooterActions = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 10px;
`;

const ConfirmOverlay = styled.div`
  position: absolute;
  inset: 0;
  background: rgba(15, 23, 42, 0.35);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 2;
`;

const ConfirmBox = styled.div`
  width: calc(100% - 32px);
  max-width: 400px;
  padding: 22px;
  border-radius: 14px;
  background: #fff;
  box-shadow: 0 16px 48px rgba(15, 23, 42, 0.2);
`;

const ConfirmText = styled.p`
  margin: 10px 0 20px;
  font-size: 13.5px;
  color: #475569;
`;
